import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div data-testid="blog-post-page">
      <Header />
      {notFound ? (
        <div className="mx-auto max-w-7xl px-5 py-32 text-center font-mono text-sm uppercase tracking-[0.25em] text-muted" data-testid="post-not-found">
          404 — FILE NOT FOUND
        </div>
      ) : !post ? (
        <div className="mx-auto max-w-7xl px-5 py-32 font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="post-loading">
          DECRYPTING<span className="animate-blink">…</span>
        </div>
      ) : (
        <article>
          <div className="relative overflow-hidden border-b border-line">
            <img src={post.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/80 to-ink" />
            <div className="relative mx-auto max-w-3xl px-5 py-24">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted transition-colors hover:text-signal">
                  <ArrowLeft size={12} /> Journal
                </Link>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-signal">{post.category}</p>
                <h1 className="mt-4 font-display text-4xl font-900 leading-[1.05] tracking-tight text-white sm:text-5xl" data-testid="post-title">
                  {post.title}
                </h1>
                <p className="mt-6 flex flex-wrap items-center gap-5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                  <span className="inline-flex items-center gap-1.5"><User size={11} /> {post.author}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock size={11} /> {post.read_time}</span>
                  <span>{new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                </p>
              </motion.div>
            </div>
          </div>
          <div className="mx-auto max-w-3xl px-5 py-16">
            <p className="border-l-2 border-signal pl-6 font-display text-xl font-500 leading-relaxed text-white">{post.excerpt}</p>
            <div className="mt-10 space-y-6" data-testid="post-content">
              {post.content.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-base leading-[1.85] text-muted">{para}</p>
              ))}
            </div>
            <div className="mt-16 border border-line bg-surface p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Disclaimer"}</p>
              <p className="mt-3 text-sm leading-relaxed text-muted">
                Analysis reflects conditions at publication. Prop firm rules change — verify the
                current rulebook on the firm's own site before purchasing an evaluation.
              </p>
            </div>
          </div>
        </article>
      )}
      <Footer />
    </div>
  );
}
